import React, { useCallback, useEffect } from 'react';
import { Button, ConfigProvider, Spin, notification } from 'antd';
import {
    getUrlParam,
    isMobile,
    isOpPortal,
    shouldShowAdvertisingIcon,
    shouldUseKlarityUi,
    isPreviewLoginPage,
} from 'utils/common';
import { Outlet, useLocation, BrowserRouter, useNavigate, useRoutes } from 'react-router-dom';
import { PATH, supportsMobile, withoutBasicAPICallingPath } from 'constants/path';
import { isPublicPath } from 'constants/ppa';
import { ROUTERS } from 'router/config';
import { GOOGLE_CLIENT_ID } from 'constants/google';
import 'styles/variable.css';
import 'styles/reset.css';
import { init } from 'utils/googleClient';
import GoogleStore from 'store/Google';
import { GoogleToken } from 'types/common';
import { setGoogleToken, setGoogleTokenExpiredAt, setSubscriptionCallbackPath, setScheduleToken } from 'utils/localstore';
import { getOpsUserPermissions } from 'api/operation';
import AccessStore from 'store/Access/permission';
import { PermissionModuleKeys } from 'constants/access';
import useSyncAccess from 'hooks/useSyncAccess';
import AccessRoleStore from 'store/Access/role';
import { NotificationPlacement } from 'antd/es/notification/interface';
import GlobalNotificationStatusStore from 'store/GlobalNotification';
import s from './app.module.less';
import { redirecToLogout } from 'utils/redirecToLogout';
import { exitPreviewProvider } from 'api/common';
import { switchProviderIdentity } from 'api/group';
import { TSwitchProviderResponse } from 'types/group';
import useSupportPracticeList from 'hooks/useSupportPracticeList';
import { handleSwitchSuccess } from 'utils/auth';
import ProviderStore from 'store/Provider';
import GlobalLayer from 'components/GlobalLayer';
import ListingFeeBanner from 'components/ListingFeeBanner';
import HeapAnalytics from 'components/HeapAnalytics';
import useTryToLoad from 'hooks/useTryToLoad';
import useCurrentUser from 'hooks/useCurrentUser';
// import KlarityPlanModal from 'components/KlarityPlanModal';
import { openKlarityPlanAdvertisingModal, openKlaritySubscriptionModal } from 'utils/globalLayerControl';
import { MODE, SHOW_KLARITY_ADVERTISING_ICON } from 'constants/common';
import CommonStore from 'store/Common';
import SubscriptionStore from 'store/Subscription';
import {
    setPreviewProviderId,
    getPreviewProviderId,
    setPreviewScheduleToken,
    getPreviewScheduleToken,
    setPreviewAdminToken,
    getPreviewAdminToken,
    setViewMode,
    getViewMode,
} from 'utils/sessionStorage';
import { ERoleCode } from 'types/enumItems';

const AdminModeBanner = () => {
    const [currentUser] = useCurrentUser();
    const [practiceList] = useSupportPracticeList();
    const [, setProviderData] = ProviderStore.useStore();
    const isActingAsOther = currentUser && currentUser.actingUser.id !== currentUser.user.id;
    const isOpsPreview = getViewMode() === MODE.OPS_PREVIEW;
    const isGroupOwner = currentUser?.roleCode === ERoleCode.GROUP_OWNER;

    React.useEffect(() => {
        if (isActingAsOther && !isOpsPreview) {
            document.body.classList.add('admin-mode-active');
        } else {
            document.body.classList.remove('admin-mode-active');
        }
        return () => {
            document.body.classList.remove('admin-mode-active');
        };
    }, [isActingAsOther, isOpsPreview]);

    if (!isActingAsOther || isOpsPreview) return null;

    const { firstName, lastName } = currentUser.actingUser;

    const handleSwitchToOwnerProvider = async () => {
        const ownerEmail = currentUser?.user?.email;
        const ownerProvider = practiceList
            ?.flatMap((p) => p.providers)
            .find((p) => p.email === ownerEmail);

        if (!ownerProvider) {
            return;
        }

        try {
            const res = await switchProviderIdentity(ownerProvider.providerId);
            if (res?.error) {
                return;
            }
            const data: TSwitchProviderResponse = res?.data?.data;
            const token = data?.token;
            const authPaylod = data?.authPaylod;
            if (authPaylod && token) {
                handleSwitchSuccess(setProviderData, authPaylod, token);
            }
            if (token) {
                setScheduleToken(token);
                window.location.reload();
            }
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <div id="admin-mode-banner" className={s.adminBannerWrap}>
            <div className={s.aLeft}>
                <div className={s.adminBannerIcon}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M8.25706 3.0976C9.02167 1.7383 10.9788 1.7383 11.7434 3.0976L17.3237 13.0181C18.0736 14.3514 17.1102 15.9987 15.5805 15.9987H4.4199C2.89025 15.9987 1.92682 14.3514 2.67675 13.0181L8.25706 3.0976ZM11.0001 12.9988C11.0001 13.5511 10.5524 13.9988 10.0001 13.9988C9.44784 13.9988 9.00012 13.5511 9.00012 12.9988C9.00012 12.4465 9.44784 11.9988 10.0001 11.9988C10.5524 11.9988 11.0001 12.4465 11.0001 12.9988ZM10.0001 4.99878C9.44784 4.99878 9.00012 5.44649 9.00012 5.99878V8.99878C9.00012 9.55106 9.44784 9.99878 10.0001 9.99878C10.5524 9.99878 11.0001 9.55106 11.0001 8.99878V5.99878C11.0001 5.44649 10.5524 4.99878 10.0001 4.99878Z"
                            fill="#FBBF24"
                        />
                    </svg>
                </div>
                <span>
                    Admin mode — You are viewing <b>{`Dr ${firstName} ${lastName}`}</b>&apos;s provider portal
                </span>
            </div>
            {isGroupOwner && (
                <div onClick={handleSwitchToOwnerProvider} className={s.aRight}>
                    Exit <b>{`Dr ${firstName} ${lastName}`}</b>&apos;s view
                </div>
            )}
        </div>
    );
};

const RouteListenerLayout = ({ children }: { children?: React.ReactElement }) => {
    const { pathname } = useLocation();
    const urlMode = getUrlParam('mode');
    const navigate = useNavigate();
    React.useEffect(() => {
        const isPreviewByOps = getViewMode() === MODE.OPS_PREVIEW;
        const noteDiv = document.getElementById('ops-attention-needed');
        if (noteDiv) {
            if (isPreviewByOps) {
                noteDiv.style.display = 'flex';
                document.body.classList.add('ops-preview-active');
            } else {
                noteDiv.style.display = 'none';
                document.body.classList.remove('ops-preview-active');
            }
        }
        if (
            isMobile() &&
            !supportsMobile.includes(pathname) &&
            !pathname.startsWith('/ppa') &&
            !pathname.includes('sign')
        ) {
            navigate(PATH.MOBILE_NOT_SUPPORT);
        }
    }, [navigate, pathname]);

    React.useEffect(() => {
        window.navigate = navigate;
    }, []);

    React.useEffect(() => {
        if (urlMode === 'dev') {
            localStorage.setItem('mode', 'dev');
        } else if (urlMode === 'clear') {
            localStorage.removeItem('mode');
            localStorage.clear();
        }
    }, [urlMode]);

    return children ?? <Outlet />;
};

const Router = ({ access, roles }: { access: Record<PermissionModuleKeys, boolean>; roles: string[] }) => {
    const routers = useRoutes([
        {
            element: <RouteListenerLayout />,
            children: ROUTERS(access, roles),
        },
    ]);

    return routers;
};

const App = () => {
    const [getCommonStore, setCommonStore] = CommonStore.useStore();
    const [api, contextHolder] = notification.useNotification();
    const [, setGoogle] = GoogleStore.useStore();
    const [getAccessStore] = AccessStore.useStore();
    const access = getAccessStore('data') as Record<PermissionModuleKeys, boolean>;
    const [saveAccess, saveRole] = useSyncAccess();
    const [getRoleStore] = AccessRoleStore.useStore();
    const [getNotificationStore] = GlobalNotificationStatusStore.useStore();
    const showDeleteNotification = getNotificationStore('data');
    const roles = getRoleStore('data') as string[];
    const [getSubscription] = SubscriptionStore.useStore();
    const currentPlan = getSubscription('currentPlan');
    const isInitingPreview = !!getUrlParam('schedule-token') && !!getUrlParam('admin-token') && !!getUrlParam('mode');
    const isSwitchAccountRedirect = window.location.pathname === PATH.SWITCH_ACCOUNT_REDIRECT;

    const handleDirectToLoginPage = useCallback((response?: any) => {
        // 一些请求写在了最外层的app.tsx里，导致login 页面也会发请求，这就意味着如果不加 !== '/', 这里会进入401死循环
        redirecToLogout();
        if (response) {
            const errorText = response.data?.message ?? response.data?.error;
            throw new Error(errorText ?? `${response.data.code} ${response.statusText}`);
        }
    }, []);

    const openNotification = useCallback(
        (placement: NotificationPlacement) => {
            api.info({
                message: 'Authorization expired',
                duration: 4,
                description: (
                    <>
                        <p className={s.loginDes}>Your login authorization has expired. Please log in again</p>
                        <div className={s.loginBtn} onClick={() => handleDirectToLoginPage()}>
                            Go to login
                        </div>
                    </>
                ),
                placement,
            });
        },
        [api, handleDirectToLoginPage],
    );

    const [loading] = useTryToLoad(isInitingPreview || isSwitchAccountRedirect);

    React.useEffect(() => {
        if (isInitingPreview) {
            setViewMode(getUrlParam('mode'));
            setPreviewScheduleToken(getUrlParam('schedule-token'));
            setPreviewAdminToken(getUrlParam('admin-token'));
            setPreviewProviderId(getUrlParam('providerId'));
            const url = new URL(window.location.href);
            url.searchParams.delete('schedule-token');
            url.searchParams.delete('admin-token');
            window.location.replace(url.toString());
        }
    }, [isInitingPreview]);

    useEffect(() => {
        if (showDeleteNotification) {
            // Check if current path is a public path to prevent notification on public pages
            const currentPath = window.location.pathname;

            // Only show notification if current path is not a public path
            if (!isPublicPath(currentPath)) {
                openNotification('topRight');
            }
        }
    }, [openNotification, showDeleteNotification]);

    const loadGsiScript = () => {
        const gsiSrc = 'https://accounts.google.com/gsi/client';
        const loadScript = (src: string) =>
            new Promise((resolve, reject) => {
                if (document.querySelector(`script[src="${src}"]`)) {
                    // eslint-disable-next-line no-promise-executor-return
                    return resolve(null);
                }
                const script = document.createElement('script');
                script.src = src;
                script.onload = () => resolve(null);
                script.onerror = (err) => reject(err);
                document.body.appendChild(script);
            });
        loadScript(gsiSrc)
            .then(() => {
                //google gsi issue
                const check = setInterval(() => {
                    const { google } = window as any;
                    if (google?.accounts) {
                        clearInterval(check);
                        init({
                            client_id: GOOGLE_CLIENT_ID,
                            scope: 'https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/cloud-platform',
                            callback: (res: GoogleToken) => {
                                setGoogle('token', res.access_token);
                                setGoogleToken(res.access_token);
                                const expiredAt = new Date().getTime() + res.expires_in * 1000;
                                setGoogle('expiredAt', expiredAt);
                                setGoogleTokenExpiredAt(expiredAt);
                            },
                        });
                        setGoogle('clientReady', true);
                        setGoogle('calendarReady', true);
                    }
                }, 300);
            })
            .catch(console.error);
    };

    const loadOpsUserAccess = useCallback(async () => {
        if (isOpPortal()) {
            const res = await getOpsUserPermissions();

            if (!res?.error) {
                const privList = res?.data?.data?.privList || [];
                const role = res?.data?.data?.roles || [];

                saveAccess(privList);
                saveRole(role);
            } else if (res?.errorCode >= 4000 && res?.errorCode < 5000) {
                handleDirectToLoginPage(res);
            }
        }
    }, [handleDirectToLoginPage]);

    const basicAPICalling = useCallback(() => {
        const { pathname } = window.location;

        if (!withoutBasicAPICallingPath.includes(pathname)) {
            loadOpsUserAccess();
        }
    }, [loadOpsUserAccess]);

    React.useEffect(() => {
        if (loading === false) {
            loadGsiScript();
            basicAPICalling();
            // 白屏 loading 结束、路由页面真正可见。路由在白屏期间就已挂载，
            // 页面内依赖“可见后才触发”的逻辑（如 meeting 页模板引导）读这个标记
            setCommonStore('appLoadingDone', true);
        }
    }, [basicAPICalling, loading]);

    const handleShowAdvertising = useCallback(() => {
        setSubscriptionCallbackPath('');
        openKlarityPlanAdvertisingModal('', () => openKlaritySubscriptionModal());
    }, []);

    const handleRemoveAdvertisingIcon = useCallback(
        (e) => {
            e.stopPropagation();
            setSubscriptionCallbackPath('');
            localStorage.setItem(SHOW_KLARITY_ADVERTISING_ICON, '2');
            setCommonStore('showAdvertisingIcon', '2');
        },
        [setCommonStore],
    );

    const isShowKlarityUi = shouldUseKlarityUi();

    React.useEffect(() => {
        //todo
    }, [isInitingPreview]);

    if (isInitingPreview) {
        return (
            <div className={s.loadingWrap}>
                <Spin spinning />
            </div>
        );
    }

    return (
        <ConfigProvider
            theme={{
                token: {
                    colorPrimary: isShowKlarityUi ? '#0E62F4' : '#006957',
                },
            }}
        >
            {contextHolder}
            <HeapAnalytics />
            <BrowserRouter>
                <div id="ops-attention-needed" className={s.notedWrap}>
                    <div className={s.noted}>
                        <div className={s.notedIcon}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M8.25706 3.0976C9.02167 1.7383 10.9788 1.7383 11.7434 3.0976L17.3237 13.0181C18.0736 14.3514 17.1102 15.9987 15.5805 15.9987H4.4199C2.89025 15.9987 1.92682 14.3514 2.67675 13.0181L8.25706 3.0976ZM11.0001 12.9988C11.0001 13.5511 10.5524 13.9988 10.0001 13.9988C9.44784 13.9988 9.00012 13.5511 9.00012 12.9988C9.00012 12.4465 9.44784 11.9988 10.0001 11.9988C10.5524 11.9988 11.0001 12.4465 11.0001 12.9988ZM10.0001 4.99878C9.44784 4.99878 9.00012 5.44649 9.00012 5.99878V8.99878C9.00012 9.55106 9.44784 9.99878 10.0001 9.99878C10.5524 9.99878 11.0001 9.55106 11.0001 8.99878V5.99878C11.0001 5.44649 10.5524 4.99878 10.0001 4.99878Z"
                                    fill="#FBBF24"
                                />
                            </svg>
                        </div>
                        <div className={s.notedInfo}>
                            <div className={s.notedTitle}>Attention needed</div>
                            <div className={s.notedDes}>
                                Preview mode — You’re previewing this provider’s portal as an internal admin. Any
                                changes you make here will update their live settings.
                            </div>
                        </div>
                        <Button
                            type="primary"
                            className={s.notedExit}
                            onClick={async () => {
                                try {
                                    await exitPreviewProvider();
                                } catch (e) {
                                    console.error(e);
                                }
                                localStorage.clear();
                                sessionStorage.clear();
                                window.close();
                            }}
                        >
                            Exit
                        </Button>
                    </div>
                </div>
                {!isPreviewLoginPage() && !isPublicPath(window.location.pathname) && <AdminModeBanner />}
                {!isPreviewLoginPage() && !isPublicPath(window.location.pathname) && !isOpPortal() && (
                    <ListingFeeBanner />
                )}
                <Router access={access} roles={roles} />
                {!isPreviewLoginPage() && !isPublicPath(window.location.pathname) && (
                    <GlobalLayer isShowKlarityUi={isShowKlarityUi} />
                )}
            </BrowserRouter>
            {(typeof loading === 'undefined' || loading) && (
                <div className={s.loadingWrap}>
                    <Spin spinning />
                </div>
            )}
            {shouldShowAdvertisingIcon(currentPlan) && (
                <div className={s.advertisingIcon} onClick={handleShowAdvertising}>
                    <div className={s.closeIcon} onClick={handleRemoveAdvertisingIcon} />
                </div>
            )}
        </ConfigProvider>
    );
};

export default App;
